import { ExhaustGraph, Turbocharger } from 'icreate/common/models/graphs';

export async function printTurboChargerGraph(graph: ExhaustGraph, echarts, oldGraph?: ExhaustGraph) {
    const turboChargerChannels = graph.TurboChannels;
    const oldGraphChannels = oldGraph ? (oldGraph.TurboChannels ?? []) : null;

    if (!turboChargerChannels || turboChargerChannels.length === 0) {
        return;
    }

    const echartsInstance = echarts.default || echarts;

    const maxLimit = findMaxLimit(turboChargerChannels);
    const minLimit = 0;
    const yAxisDecimalPlaces = findMaxDecimalPlaces(turboChargerChannels);

    const { notBorderedLables, borderedLables, changedIndexes } = prepareXAxislabel(
        turboChargerChannels,
        oldGraphChannels
    );

    const channelValues = generateChannelValues(turboChargerChannels);
    const valueLabels = prepareValueLabels(channelValues);
    const graphValuesArray = prepareGraphValuesArray(channelValues);

    let graphYAxisMax = Number(maxLimit);
    let graphYAxisMin = Number(minLimit);

    if (isNaN(graphYAxisMax)) graphYAxisMax = 500;
    if (isNaN(graphYAxisMin)) graphYAxisMin = 0;
    if (graphYAxisMax <= graphYAxisMin) {
        graphYAxisMax = graphYAxisMin + 100;
    }

    const calculatedInterval = (graphYAxisMax - graphYAxisMin) / 6;
    const safeInterval = calculatedInterval > 0 ? calculatedInterval : 50;

    const titleBorder =
        oldGraph && oldGraph?.title != graph.title ? [true, true, true, true] : [false, false, false, false];

    const barGraphDivision = document.createElement('div');
    barGraphDivision.style.height = '490pt';
    barGraphDivision.style.width = '600pt';

    const myChart = echartsInstance.init(barGraphDivision, null, { width: 600, height: 490 });

    const option = {
        color: ['black'],
        grid: {
            bottom: 60,
            right: 10,
        },
        xAxis: [
            {
                type: 'category',
                data: notBorderedLables.data,
                position: 'bottom',
                axisTick: { show: false },
            },
            {
                type: 'category',
                data: borderedLables.data,
                position: 'bottom',
                axisTick: { show: false },
            },
            {
                type: 'category',
                data: valueLabels.data,
                position: 'bottom',
                offset: 22,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { formatter: () => '' },
            },
        ],
        yAxis: {
            type: 'value',
            max: graphYAxisMax,
            min: graphYAxisMin,
            interval: safeInterval,
            axisLine: { lineStyle: { color: 'black' } },
            splitLine: { lineStyle: { color: 'black' } },
            axisLabel: {
                formatter: (value: number) => {
                    return value.toFixed(yAxisDecimalPlaces);
                },
            },
            name: '°C',
            nameLocation: 'end',
            nameGap: 15,
            nameTextStyle: {
                align: 'left',
                verticalAlign: 'middle',
                padding: [0, 0, 0, -20],
            },
        },
        series: [
            {
                data: graphValuesArray,
                type: 'bar',
            },
        ],
    };

    myChart.setOption(option);

    await new Promise(resolve => setTimeout(resolve, 500));

    const dataUrl = myChart.getDataURL({
        type: 'png',
        pixelRatio: 1,
        backgroundColor: '#fff',
    });

    const legendTable = buildLegendTable(turboChargerChannels, changedIndexes);

    return {
        body: [
            [
                [
                    {
                        columns: [
                            { width: '*', text: '' },
                            {
                                width: 'auto',
                                table: {
                                    body: [
                                        [
                                            {
                                                text: graph.title,
                                                border: titleBorder,
                                                borderColor: ['red', 'red', 'red', 'red'],
                                                fontSize: 15,
                                                alignment: 'center',
                                                noWrap: true,
                                            },
                                        ],
                                    ],
                                },
                            },
                            { width: '*', text: '' },
                        ],
                        margin: [0, 10, 0, 10],
                    },
                    {
                        columnGap: 8,
                        columns: [
                            {
                                width: 'auto',
                                table: {
                                    body: [
                                        [
                                            {
                                                image: dataUrl,
                                                width: 655,
                                                height: 440,
                                                border: [false, false],
                                            },
                                        ],
                                    ],
                                    alignment: 'center',
                                },
                                layout: {
                                    paddingLeft: () => 0,
                                    paddingRight: () => 0,
                                    paddingTop: () => 0,
                                    paddingBottom: () => 0,
                                },
                            },
                            {
                                width: 'auto',
                                table: legendTable,
                                layout: {
                                    defaultBorder: false,
                                    paddingLeft: () => 4,
                                    paddingRight: () => 4,
                                    paddingTop: () => 2,
                                    paddingBottom: () => 2,
                                },
                            },
                        ],
                    },
                ],
            ],
        ],
        widths: [763],
        heights: [490],
    };
}

function prepareXAxislabel(channels: Turbocharger[], oldChannels?: Turbocharger[]) {
    let newChannelsIds = new Set();
    let deletedChannelsLength = 0;

    let borderedLables = { data: [] as string[], customValues: [] as number[] };
    let notBorderedLables = { data: [] as string[], customValues: [] as number[] };
    const changedIndexes: Set<number> = new Set();

    if (oldChannels) {
        const oldChannelsArray = oldChannels.map(channel => channel?.tempChannel);
        const oldChannelsSet = new Set(oldChannelsArray);

        channels.forEach(channel => {
            const fcuChannelId = channel?.tempChannel?.fcuData?.fcuChannelId;
            if (fcuChannelId && !oldChannelsSet.has(channel?.tempChannel)) newChannelsIds.add(fcuChannelId);
        });

        deletedChannelsLength = Math.max(oldChannels.length - channels.length, 0);
    }

    channels.forEach((channel: Turbocharger, index) => {
        const fcuChannelId = channel?.tempChannel?.fcuData?.fcuChannelId;
        const position = String(index + 1);
        if (fcuChannelId && newChannelsIds.has(fcuChannelId)) {
            borderedLables.data.push(position);
            borderedLables.customValues.push(index);
            notBorderedLables.data.push('');
            changedIndexes.add(index);
        } else {
            notBorderedLables.data.push(position);
            notBorderedLables.customValues.push(index);
            borderedLables.data.push('');
        }
    });

    for (let i = 0; i < deletedChannelsLength; i++) {
        notBorderedLables.data.push('');
        borderedLables.data.push(String(channels.length + i + 1));
        borderedLables.customValues.push(channels.length + i);
    }

    return { borderedLables, notBorderedLables, changedIndexes };
}

function buildLegendTable(channels: Turbocharger[], changedIndexes: Set<number>) {
    const border = [true, true, true, true] as [boolean, boolean, boolean, boolean];
    const firstValueChanged = changedIndexes.has(0);
    const borderColor = ['black', 'black', 'black', firstValueChanged ? 'red' : 'black'] as [
        string,
        string,
        string,
        string,
    ];
    const header = [
        { text: '#', fontWeight: 'bold', border, borderColor },
        { text: 'Ch. Tag.', fontWeight: 'bold', border, borderColor },
    ];

    const rows = channels.map((channel, index) => {
        const isChanged = changedIndexes.has(index);
        const topRowChanged = changedIndexes.has(index - 1);
        const bottomRowChanged = changedIndexes.has(index + 1);

        const topRowColor = isChanged || topRowChanged ? 'red' : 'black';
        const bottomRowColor = isChanged || bottomRowChanged ? 'red' : 'black';
        const rowBorderColor = [
            isChanged ? 'red' : 'black',
            topRowColor,
            isChanged ? 'red' : 'black',
            bottomRowColor,
        ] as [string, string, string, string];

        return [
            { text: String(index + 1), border, borderColor: rowBorderColor },
            { text: channel?.tempChannel?.fcuData?.fcuChannelId ?? '', border, borderColor: rowBorderColor },
        ];
    });

    return {
        widths: [10, 50],
        body: [header, ...rows],
    };
}

function getDecimalPlaces(channel: Turbocharger): number {
    const scale = channel?.tempChannel?.fcuData?.scale || 1;
    return Math.max(Math.round(Math.log10(scale)), 0);
}

function findMaxLimit(turboChargerChannels: Turbocharger[]): string {
    const maxValues = turboChargerChannels.map((channel: Turbocharger) => {
        const scale = channel?.tempChannel?.fcuData?.scale || 1;
        const maxScaled = (channel?.tempChannel?.fcuData?.measurableRange?.max || 500) / scale;
        return maxScaled;
    });

    const sortedNumbers = maxValues.sort((a, b) => b - a);
    return String(sortedNumbers[0] ?? 500);
}

function findMaxDecimalPlaces(turboChargerChannels: Turbocharger[]): number {
    return turboChargerChannels.reduce((max, channel) => Math.max(max, getDecimalPlaces(channel)), 0);
}

function generateChannelValues(turboChargerChannels: Turbocharger[]) {
    return turboChargerChannels.map((channel: Turbocharger) => {
        if (!channel?.tempChannel) return { channel: null, value: 0 };
        const measurableRange = channel?.tempChannel?.fcuData?.measurableRange;
        const min = measurableRange?.min ?? 0;
        const max = measurableRange?.max ?? 500;
        const scale = channel?.tempChannel?.fcuData?.scale || 1;
        const decimalPlaces = getDecimalPlaces(channel);

        const randomRaw = Math.floor(Math.random() * (max - min + 1)) + min;
        const scaledValue = Number((randomRaw / scale).toFixed(decimalPlaces));

        return { channel, value: isNaN(scaledValue) ? 0 : scaledValue };
    });
}

function prepareValueLabels(channelValues: { channel: Turbocharger; value: number }[]) {
    return {
        data: channelValues.map(({ channel, value }) =>
            value && channel ? value.toFixed(getDecimalPlaces(channel)) : ''
        ),
    };
}

function prepareGraphValuesArray(channelValues: { channel: Turbocharger; value: number }[]) {
    return channelValues.map(({ value }) => (isNaN(value) ? 0 : value));
}
